






















































































import Vue from "vue";
import LayoutColumnSingle from "@/components/Atoms/Layout/ColumnSingle/index.vue";
import Heading from "@/components/Atoms/Typography/Heading/index.vue";
import Suffix from "@/components/Atoms/Typography/Suffix/index.vue";
import Tabs from "@/components/Molecules/Tabs/index.vue";
import TabPane from "@/components/Molecules/Tabs/TabPane/index.vue";
import LoadingView from "@/components/Molecules/LoadingView/index.vue";
import CompanyInfos from "./Molecules/CompanyInfos/index.vue";
import ReviewCompare from "./Molecules/ReviewCompare/index.vue";
import FavoriteCompare from "./Molecules/FavoriteCompare/index.vue";
import ReviewContents, {
  Props as ReviewContentsProps
} from "@/components/Pages/CompanyDetail/Organisms/ReviewContents/index.vue";
import CompanyCompareButton from "@/components/Molecules/CompanyCompareButton/index.vue";
import {
  getBusinessCategoriesAverages,
  getCompanyDetail,
  getCompanyDetailV2
} from "@/api";
import { INIT_REVIEW } from "@/constants/company";
import {
  ApiResponse,
  CompanyDetailReviewCategory,
  UserReports
} from "@/api/types";

export interface UserReportsWithName extends UserReports {
  name: string;
}

export interface Data {
  loading: {
    page: boolean;
  };
  activeTab: number;
  company?: ApiResponse.CompanyDetail;
  comparedCompany?: ApiResponse.CompanyDetail;
  average?: ApiResponse.BusinessCategoriesAverages;
  review: UserReportsWithName[];
  scrollAreaElement?: HTMLElement;
}

export interface Computed {
  partnerUser?: ApiResponse.PartnerUser;
  isOurCompany: boolean;
  compareReviews?: { label: string; reviewGraph: ApiResponse.ReviewGraph[] }[];
  compareFavorites?: {
    label: string;
    favoriteGraph: ApiResponse.FavoriteGraph[];
  }[];
}

export interface Props {}
export interface Methods {
  getReviewCount: (index: number) => number;
  getCompanyReviewCount: (index: number) => number;
  getComparedCompanyReviewCount: (index: number) => number;
  compareCompaniesForReviewContents: (
    index: number
  ) => ReviewContentsProps["compareCompanies"];
  reviewLabel: (index: number) => string;
  tabState: (index: number, category: CompanyDetailReviewCategory) => boolean;
  getCompanyCompare: () => void;
  reviewShaping: (review: UserReports[]) => UserReportsWithName[];
  dataReset: () => void;
  handleScroll: (top: number) => void;
}

type ScrollAreaInstance = Vue | undefined;

export default Vue.extend<Data, Methods, Computed, Props>({
  name: "CompanyDetail",
  components: {
    LayoutColumnSingle,
    CompanyInfos,
    Heading,
    Suffix,
    CompanyCompareButton,
    Tabs,
    TabPane,
    LoadingView,
    ReviewCompare,
    FavoriteCompare,
    ReviewContents
  },
  data() {
    return {
      loading: {
        page: false
      },
      company: undefined,
      comparedCompany: undefined,
      average: undefined,
      review: [],
      activeTab: 0,
      scrollAreaElement: undefined
    };
  },
  computed: {
    partnerUser() {
      return this.$store.state.domains.user.partnerUser;
    },
    isOurCompany() {
      if (!this.company || !this.partnerUser) {
        return false;
      }
      return this.company.id === this.partnerUser.partner.company.id;
    },
    compareReviews() {
      if (!this.company) {
        return undefined;
      }

      if (this.$route.query.type === "average" && this.average) {
        return [
          { label: this.company.name, reviewGraph: this.company.reviews },
          { label: "業界平均", reviewGraph: this.average.reviews }
        ];
      }

      if (this.$route.query.type === "company" && this.comparedCompany) {
        return [
          { label: this.company.name, reviewGraph: this.company.reviews },
          {
            label: this.comparedCompany.name,
            reviewGraph: this.comparedCompany.reviews
          }
        ];
      }

      return undefined;
    },
    compareFavorites() {
      if (!this.company) {
        return undefined;
      }

      if (this.$route.query.type === "average" && this.average) {
        return [
          { label: this.company.name, favoriteGraph: this.company.favorites },
          { label: "業界平均(上位10社)", favoriteGraph: this.average.favorites }
        ];
      }

      if (this.$route.query.type === "company" && this.comparedCompany) {
        return [
          { label: this.company.name, favoriteGraph: this.company.favorites },
          {
            label: this.comparedCompany.name,
            favoriteGraph: this.comparedCompany.favorites
          }
        ];
      }

      return undefined;
    }
  },
  mounted() {
    this.getCompanyCompare();

    this.$watch(
      () => [this.$route.query.type, this.$route.query.companyId],
      () => {
        this.getCompanyCompare();
      }
    );
  },
  methods: {
    getCompanyReviewCount(index) {
      if (!this.company) {
        return 0;
      }
      const review = this.reviewShaping(this.company.user_reports);
      return review[index].count;
    },
    getComparedCompanyReviewCount(index) {
      if (!this.comparedCompany) {
        return 0;
      }
      const review = this.reviewShaping(this.comparedCompany.user_reports);
      return review[index].count;
    },
    compareCompaniesForReviewContents(index) {
      if (!this.company || !this.comparedCompany) {
        return;
      }
      return [
        { ...this.company, reviewCount: this.getCompanyReviewCount(index) },
        {
          ...this.comparedCompany,
          reviewCount: this.getComparedCompanyReviewCount(index)
        }
      ];
    },
    getReviewCount(index) {
      return (
        this.getCompanyReviewCount(index) +
        this.getComparedCompanyReviewCount(index)
      );
    },
    reviewLabel(index) {
      return `${this.review[index].name}のクチコミ（${this.getReviewCount(
        index
      )}件）`;
    },
    tabState(index, category) {
      return this.review[index].category === category;
    },
    async getCompanyCompare() {
      this.dataReset();

      if (this.$route.query.type === "average") {
        this.$set(this.loading, "page", true);

        const { data: company } = await getCompanyDetailV2({
          company_id: this.$route.params.companyId
        });

        const { data: average } = await getBusinessCategoriesAverages({
          business_category_id: company.business_category.id
        });

        this.company = company;
        this.average = average;
        this.review = this.reviewShaping(company.user_reports);

        this.$set(this.loading, "page", false);
      } else if (
        this.$route.query.type === "company" &&
        this.$route.query.companyId
      ) {
        this.$set(this.loading, "page", true);

        const [
          { data: company },
          { data: comparedCompany }
        ] = await Promise.all([
          getCompanyDetail({
            company_id: this.$route.params.companyId
          }),
          getCompanyDetail({
            company_id: String(this.$route.query.companyId)
          })
        ]);

        this.company = company;
        this.comparedCompany = comparedCompany;

        const mergedUserReports = [
          ...company.user_reports,
          ...comparedCompany.user_reports
        ].reduce(
          (acc: { [category: string]: UserReports }, cur: UserReports) => {
            const nextAcc = {
              ...acc,
              [cur.category]: {
                ...cur,
                count: cur.count + (acc[cur.category]?.count || 0)
              }
            };
            return nextAcc;
          },
          {}
        );
        this.review = this.reviewShaping(Object.values(mergedUserReports));

        this.$set(this.loading, "page", false);

        this.$nextTick(() => {
          const $scrollArea = (this.$refs.scrollArea as ScrollAreaInstance)
            ?.$el as HTMLElement | undefined;

          if ($scrollArea) {
            this.scrollAreaElement = $scrollArea;
          }
        });
      }
    },
    reviewShaping(review) {
      return INIT_REVIEW.map(e => {
        const count = review.find(f => e.category === f.category);
        return {
          category: e.category,
          name: e.name,
          count: count ? count.count : 0
        };
      });
    },
    dataReset() {
      this.company = undefined;
      this.comparedCompany = undefined;
      this.average = undefined;
      this.review = [];
      this.activeTab = 0;
    },
    handleScroll(top) {
      const $scrollArea = (this.$refs.scrollArea as ScrollAreaInstance)?.$el as
        | HTMLElement
        | undefined;

      if ($scrollArea) {
        $scrollArea.scrollTo({
          top: top
        });
      }
    }
  }
});
